/**
 * useRequireAuth - consistent gate for authenticated write-actions.
 *
 * Returns a `requireAuth(action, options?)` function:
 *   - If the user is authenticated, runs `action()` and returns its result.
 *   - If not, redirects to /login carrying a `returnTo` (defaults to the current
 *     path + query) so the user lands back where they were after signing in,
 *     then can complete the action.
 *
 * Reuses the shared AuthContext (no duplicate auth state).
 *
 * NOTE: this is for WRITE actions only (favorite, review, customize/build).
 * Browsing and guest booking must NOT use this - they stay fully public.
 */

import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function useRequireAuth() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (action, options = {}) => {
      if (isAuthenticated) {
        return action?.();
      }
      const returnTo = options.returnTo ?? location.pathname + location.search;
      navigate('/login', { state: { returnTo, ...(options.state || {}) } });
      return undefined;
    },
    [isAuthenticated, navigate, location]
  );
}
